import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import {
  UserSurvey, UserSurveyStatuses, AppSurveyStatuses,
} from '@gamiphy/bl/survey';
import { SurveyApi } from '@gamiphy/gamix-modules/survey';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { AppSurveyDto, SurveysApi, UserSurveysApi } from '@coretava/api/events-service';

class SurveyStore {
  @observable initialized = false;

  @observable started = false;

  @observable loading = false;

  @observable appSurvey?: AppSurveyDto = undefined;

  @observable userSurvey?: UserSurvey = undefined;

  constructor() {
    makeAutoObservable(this);

    when(() => AppStore.initialized && !AppStore.isCashbackProgram, () => {
      void this.initialize();
    });

    observe(UserStore, 'user', async () => {
      if (UserStore.user && this.appSurvey) {
        const userSurvey = await SurveyApi.getUserSurvey(this.appSurvey.id);

        runInAction(() => {
          this.userSurvey = userSurvey;
        });
      }
    });
  }

  @computed
  get surveysApi(): SurveysApi {
    return new SurveysApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get userSurveysApi(): UserSurveysApi {
    return new UserSurveysApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed get isAvailable(): boolean {
    return !!this.userSurvey && this.userSurvey.status !== UserSurveyStatuses.submitted;
  }

  @computed get completed(): boolean {
    return !!this.userSurvey && this.userSurvey.status === UserSurveyStatuses.submitted;
  }

  @computed get isSubmitted(): boolean {
    return !!this.userSurvey && this.userSurvey.status === UserSurveyStatuses.submitted;
  }

  @computed get progress(): number {
    return (this.userSurvey && this.appSurvey)
      ? Math.round((100 * this.userSurvey.answers.length) / this.appSurvey.sections[0].questions.length)
      : 0;
  }

  @computed
  public get active(): boolean {
    return this.appSurvey?.status === AppSurveyStatuses.active;
  }

  @action
  async initialize(): Promise<void> {
    const response = await this.surveysApi.appSurveysControllerList(AppStore.app.id);

    runInAction(() => {
      [this.appSurvey] = response.data.surveys;
    });

    if (this.appSurvey) {
      await when(() => UserStore.isLoggedIn);
      const userSurvey = await SurveyApi.getUserSurvey(this.appSurvey.id);

      runInAction(() => {
        this.userSurvey = userSurvey;
      });
    }

    runInAction(() => {
      this.initialized = true;
    });
  }

  @action
  async answerQuestion(questionId: string, answer: string | string[]): Promise<void> {
    if (this.userSurvey && this.appSurvey) {
      try {
        this.loading = true;
        this.userSurvey = await SurveyApi.answerQuestion(this.userSurvey.id, questionId, answer);
      } finally {
        this.loading = false;
      }
    }
  }

  @action
  async submitSurvey(): Promise<void> {
    if (this.userSurvey && this.appSurvey) {
      try {
        this.loading = true;
        this.userSurvey = await SurveyApi.submitSurvey(this.userSurvey.id);
      } finally {
        this.loading = false;
      }
    }
  }
}

export default new SurveyStore();
